import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Checkbox,
  Divider,
  Grid
} from '@mui/material';
import {
  Monitor as MonitorIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Computer as ComputerIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { getRegionInfo } from '../api/services/computeService/LocationService';

export default function ServerCard({
  server,
  isSelected,
  isDeleting,
  hasPendingOperation,
  onSelect,
  onDelete,
  onRefresh,
  showMonitor = true
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMonitorDisabled = server.status === 'Registering' || server.status === 'Registration Failed' || isDeleting || hasPendingOperation;
  const isSelectionDisabled = server.status === 'Registering' || isDeleting || hasPendingOperation;
  const isDeleteDisabled = isDeleting || hasPendingOperation;

  // Get region information for display
  const regionInfo = getRegionInfo(server.region);

  const getStatusChip = () => {
    if (isDeleting || hasPendingOperation) {
      return (
        <Chip
          label="Deleting..."
          size="small"
          color="warning"
          icon={<LinearProgress size={16} thickness={2} />}
        />
      );
    }

    if (server.status === 'Registering') {
      return (
        <Chip
          label="Registering"
          size="small"
          color="warning"
          icon={<HourglassEmptyIcon fontSize="small" />}
        />
      );
    }

    if (server.status === 'Registration Failed') {
      return (
        <Chip
          label="Failed"
          size="small"
          color="error"
          icon={<ErrorIcon fontSize="small" />}
        />
      );
    }

    if (server.status === 'Active') {
      return (
        <Chip
          label="Active"
          size="small"
          color="success"
          icon={<CheckCircleIcon fontSize="small" />}
        />
      );
    }

    return (
      <Chip
        label={server.status || "Unknown"}
        size="small"
        color="default"
      />
    );
  };

  const MetricItem = ({ icon, label, value, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color: color || 'text.secondary' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        opacity: isDeleteDisabled ? 0.6 : 1,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: isDeleteDisabled ? theme.palette.divider : theme.palette.action.hover,
          transform: isDeleteDisabled ? 'none' : 'translateY(-2px)',
          boxShadow: isDeleteDisabled ? 'none' : 4
        }
      }}
    >
      <CardContent sx={{ flex: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={isSelected}
              onChange={() => onSelect(server.id)}
              disabled={isSelectionDisabled}
              size="small"
              sx={{ p: 0 }}
            />
            {getStatusChip()}
          </Box>
          <Tooltip title={regionInfo.name}>
            <Chip
              icon={<LocationIcon fontSize="small" />}
              label={regionInfo.name || server.region || 'Unknown'}
              size="small"
              variant="outlined"
              sx={{
                '& .MuiChip-icon': {
                  color: theme.palette.info.main
                }
              }}
            />
          </Tooltip>
        </Box>

        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 'bold' }}>
          {server.hostname || 'No hostname'}
        </Typography>
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {server.ipAddress || 'No IP'} • {server.id}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MetricItem 
              icon={<SpeedIcon fontSize="small" />}
              label="GPUs"
              value={server.numGpus || 0}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricItem 
              icon={<ComputerIcon fontSize="small" />}
              label="CPUs"
              value={server.numOfCpus || server.cpus || 0}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricItem 
              icon={<MemoryIcon fontSize="small" />}
              label="Memory"
              value={`${server.mem || 0} GB`}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricItem 
              icon={<StorageIcon fontSize="small" />}
              label="Storage"
              value={(() => {
                const storageGB = server.storage || 0;
                if (storageGB >= 1000) {
                  return `${(storageGB / 1000).toFixed(1)} TB`;
                }
                return `${storageGB} GB`;
              })()}
              color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>

        {(server.gpuBreakdown?.length > 0 || server.cpuModel) && (
          <Box sx={{ mt: 2 }}>
            <Stack spacing={1}>
              {server.gpuBreakdown?.length > 0 && (
                <>
                  {server.gpuBreakdown.map((gpu, index) => (
                    <Chip 
                      key={index}
                      label={`GPU: ${gpu.model} ${gpu.count > 1 ? `×${gpu.count}` : ''}`} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                      icon={<SpeedIcon fontSize="small" />}
                    />
                  ))}
                </>
              )}
              {server.cpuModel && (
                <Chip 
                  label={`CPU: ${server.cpuModel}`} 
                  size="small" 
                  color="secondary"
                  variant="outlined"
                  sx={{ width: '100%', justifyContent: 'flex-start' }}
                  icon={<ComputerIcon fontSize="small" />}
                />
              )}
            </Stack>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          {showMonitor && (
            <Tooltip title="Monitor">
              <IconButton
                size="small"
                onClick={() => navigate(`/monitoring/${server.id}`)}
                disabled={isMonitorDisabled}
                sx={{
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' }
                }}
              >
                <MonitorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {onDelete && (
            <Tooltip title={isDeleting ? "Deleting..." : "Delete"}>
              <IconButton
                size="small"
                onClick={() => onDelete(server.id)}
                disabled={isDeleteDisabled}
                sx={{
                  color: 'error.main',
                  '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </CardActions>
    </Card>
  );
} 